import { constants, type infra } from '@photo-vault/shared';
import { EncryptWorkerPool } from './workerPool.js';
import {
  deleteSession,
  newSessionId,
  saveSession,
  type PersistedFile,
  type PersistedSession,
} from './persistence.js';
import { retryWithBackoff, runWithConcurrency } from './retry.js';

// Batch size 50 keeps each /uploads/batch round-trip safely under the API's
// 100-item cap and bounds Lambda fan-out (each item costs one S3
// CreateMultipartUpload + N presigns). For 1k photos this is ~20 batch + 20
// complete = ~40 backend calls — well under the plan's 250 budget.
const FILE_BATCH_SIZE = 50;
// Concurrency for part PUTs across the entire batch (not per file). Plan
// targets 8-16; we sit in the middle.
const PART_CONCURRENCY = 12;
// Crypto is CPU-bound; 4 workers keeps the main thread free without saturating
// typical 4-8 core devices.
const ENCRYPT_POOL_SIZE = 4;

export interface BulkUploadInput {
  files: File[];
  albumId: string;
  albumKey: CryptoKey;
  token: string;
  /** Metadata RPC surface (presign + complete-notify). */
  metadata: infra.MetadataStore;
  /** Storage driver (S3 multipart PUT/Complete). */
  storage: infra.StorageDriver;
  onProgress?: (snapshot: BulkProgress) => void;
  signal?: AbortSignal;
  // Reuse an existing IDB session id (resume path). When omitted a new
  // session is created.
  sessionId?: string;
}

export interface FileProgress {
  clientFileId: string;
  filename: string;
  size: number;
  status: PersistedFile['status'];
  uploadedBytes: number;
  totalBytes: number;
  error?: string;
}

export interface BulkProgress {
  sessionId: string;
  files: FileProgress[];
  encrypted: number;
  uploaded: number;
  total: number;
}

interface PreparedFile {
  clientFileId: string;
  file: File;
  encryptedBytes: Uint8Array;
  encryptedMeta: string;
  partsCount: number;
}

export async function uploadBulk(input: BulkUploadInput): Promise<BulkProgress> {
  const sessionId = input.sessionId ?? newSessionId();
  const partSize = constants.MULTIPART_PART_SIZE_BYTES;

  const persistedFiles: PersistedFile[] = input.files.map((f) => ({
    clientFileId: crypto.randomUUID(),
    filename: f.name,
    size: f.size,
    status: 'pending',
    completedParts: [],
  }));

  const session: PersistedSession = {
    id: sessionId,
    albumId: input.albumId,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    files: persistedFiles,
  };
  await saveSession(session);

  const fileProgress: FileProgress[] = persistedFiles.map((p, i) => ({
    clientFileId: p.clientFileId,
    filename: p.filename,
    size: input.files[i]!.size,
    status: 'pending',
    uploadedBytes: 0,
    totalBytes: input.files[i]!.size,
  }));

  const emit = () =>
    input.onProgress?.({
      sessionId,
      files: fileProgress.slice(),
      encrypted: fileProgress.filter((f) => f.status !== 'pending').length,
      uploaded: fileProgress.filter((f) => f.status === 'complete').length,
      total: fileProgress.length,
    });

  const pool = new EncryptWorkerPool(ENCRYPT_POOL_SIZE);
  try {
    for (let batchStart = 0; batchStart < input.files.length; batchStart += FILE_BATCH_SIZE) {
      if (input.signal?.aborted) throw new DOMException('aborted', 'AbortError');
      const batchEnd = Math.min(batchStart + FILE_BATCH_SIZE, input.files.length);
      const batchFiles = input.files.slice(batchStart, batchEnd);
      const batchPersisted = persistedFiles.slice(batchStart, batchEnd);
      const batchProgress = fileProgress.slice(batchStart, batchEnd);

      // 1. Encrypt the batch in parallel through the worker pool.
      const prepared = await runWithConcurrency<File, PreparedFile>(
        batchFiles,
        ENCRYPT_POOL_SIZE,
        async (file, idx) => {
          const persisted = batchPersisted[idx]!;
          const progress = batchProgress[idx]!;
          const payload = await pool.encrypt(file, input.albumKey);
          const partsCount = Math.max(1, Math.ceil(payload.bytes.byteLength / partSize));
          if (partsCount > constants.MULTIPART_MAX_PARTS) {
            throw new Error(
              `${file.name}: ${partsCount} parti eccede il massimo di ${constants.MULTIPART_MAX_PARTS}`,
            );
          }
          persisted.status = 'encrypted';
          persisted.partsCount = partsCount;
          progress.status = 'encrypted';
          progress.totalBytes = payload.bytes.byteLength;
          emit();
          return {
            clientFileId: persisted.clientFileId,
            file,
            encryptedBytes: payload.bytes,
            encryptedMeta: payload.encryptedMeta,
            partsCount,
          };
        },
      );
      await saveSession(session);

      // 2. One round-trip presign for the whole batch.
      const batchResp = await input.metadata.uploadsBatch(
        {
          items: prepared.map((p) => ({
            albumId: input.albumId,
            clientFileId: p.clientFileId,
            sizeBytes: p.encryptedBytes.byteLength,
            partsHint: p.partsCount,
            encryptedMeta: p.encryptedMeta,
          })),
        },
        input.token,
      );

      const byClientId = new Map(batchResp.items.map((it) => [it.clientFileId, it]));
      for (const persisted of batchPersisted) {
        const r = byClientId.get(persisted.clientFileId);
        if (!r) throw new Error(`backend non ha restituito il file ${persisted.filename}`);
        persisted.photoId = r.photoId;
        persisted.uploadId = r.uploadId;
        persisted.status = 'uploading';
      }
      const progressByClientId = new Map(batchProgress.map((p) => [p.clientFileId, p]));
      for (const p of batchProgress) p.status = 'uploading';
      await saveSession(session);
      emit();

      // 3. Build a flat job list across the batch and upload parts in
      //    parallel — this keeps S3 saturated even when one file's parts
      //    finish early.
      interface PartJob {
        clientFileId: string;
        partNumber: number;
        url: string;
        chunk: Uint8Array;
      }
      const jobs: PartJob[] = [];
      const uploadResultByClientId = new Map<
        string,
        { uploadId: string; parts: { partNumber: number; etag: string }[] }
      >();
      for (const p of prepared) {
        const r = byClientId.get(p.clientFileId)!;
        uploadResultByClientId.set(p.clientFileId, { uploadId: r.uploadId, parts: [] });
        for (let i = 0; i < r.partUrls.length; i++) {
          const start = i * partSize;
          const end = Math.min(start + partSize, p.encryptedBytes.byteLength);
          jobs.push({
            clientFileId: p.clientFileId,
            partNumber: i + 1,
            url: r.partUrls[i]!,
            chunk: p.encryptedBytes.subarray(start, end),
          });
        }
      }

      const completeUrlByClientId = new Map(
        batchResp.items.map((it) => [it.clientFileId, it.completeUrl]),
      );

      await runWithConcurrency(jobs, PART_CONCURRENCY, async (job) => {
        const { etag } = await retryWithBackoff(
          () => input.storage.uploadPart(job.url, job.chunk, input.signal),
          input.signal ? { signal: input.signal } : {},
        );
        const result = uploadResultByClientId.get(job.clientFileId)!;
        result.parts.push({ partNumber: job.partNumber, etag });
        // Persist progress incrementally so a tab crash leaves a usable
        // resume point even mid-batch.
        const persisted = batchPersisted.find((b) => b.clientFileId === job.clientFileId)!;
        persisted.completedParts.push({ partNumber: job.partNumber, etag });
        const progress = progressByClientId.get(job.clientFileId)!;
        progress.uploadedBytes += job.chunk.byteLength;
        emit();
      });
      await saveSession(session);

      // 4. CompleteMultipartUpload per file, against the presigned URL
      // returned by /uploads/batch. Doing this from the client closes the
      // window where the backend used to make this call: a tab crash here
      // simply leaves an aborted multipart upload (S3 lifecycle cleans
      // up), instead of an orphan completed object with no DDB row.
      await runWithConcurrency(prepared, PART_CONCURRENCY, async (p) => {
        const completeUrl = completeUrlByClientId.get(p.clientFileId)!;
        const result = uploadResultByClientId.get(p.clientFileId)!;
        await retryWithBackoff(
          () => input.storage.completeMultipart(completeUrl, result.parts),
          input.signal ? { signal: input.signal } : {},
        );
      });

      // 5. Legacy fallback: flip Photo rows for buckets that don't yet have
      // the EventBridge rule. For new buckets onS3Upload Lambda has likely
      // already flipped them and this becomes a no-op (markPhotoComplete
      // is idempotent).
      await input.metadata.uploadsComplete(
        {
          items: prepared.map((p) => {
            const r = byClientId.get(p.clientFileId)!;
            const result = uploadResultByClientId.get(p.clientFileId)!;
            // S3 requires parts in ascending order on CompleteMultipartUpload.
            const parts = result.parts.slice().sort((a, b) => a.partNumber - b.partNumber);
            return {
              albumId: input.albumId,
              photoId: r.photoId,
              uploadId: r.uploadId,
              parts,
            };
          }),
        },
        input.token,
      );

      for (const persisted of batchPersisted) persisted.status = 'complete';
      for (const p of batchProgress) p.status = 'complete';
      await saveSession(session);
      emit();
    }
  } finally {
    pool.terminate();
  }

  await deleteSession(sessionId);
  return {
    sessionId,
    files: fileProgress,
    encrypted: fileProgress.length,
    uploaded: fileProgress.filter((f) => f.status === 'complete').length,
    total: fileProgress.length,
  };
}
